import {
  BillableEvent,
  Invoice,
  InvoiceInterface,
  InvoiceItem,
  InvoiceItemInterface,
  InvoiceStatus,
  InvoiceSummary,
  InvoiceSummaryInterface,
  Page
} from 'types';
import { refreshToken } from './auth';
import { authorizedFetch, serverMessage } from './authorized-fetch';
import fetchWithRetry from './fetch-retry';

export const generateInvoices = async (params: {
  customerId?: number;
  from: Date;
  to: Date;
  locationId?: number;
}): Promise<InvoiceSummary[] | null> => {
  const url = new URL('/api/invoices/generate', window.location.origin);
  url.searchParams.append('fromDate', params.from.toISOString());
  url.searchParams.append('toDate', params.to.toISOString());
  if (params.customerId !== undefined) {
    url.searchParams.append('customerId', `${params.customerId}`);
  }
  if (params.locationId !== undefined) {
    url.searchParams.append('locationId', `${params.locationId}`);
  }

  // /generate returns the summary projection, not the full Invoice graph. See the note in
  // use-invoice-api.tsx.
  return fetchWithRetry<InvoiceSummaryInterface[] | null>(url, {}, null).then(
    invoices => invoices?.map(invoice => new InvoiceSummary(invoice)) ?? null
  );
};

export const generateUnitInvoices = async (params: {
  unitId: number;
  locationId: number;
  from: Date;
  to: Date;
}): Promise<InvoiceItem[]> => {
  const url = new URL(`/api/invoices/unit/${params.unitId}/generate`, window.location.origin);
  url.searchParams.append('fromDate', params.from.toISOString());
  url.searchParams.append('toDate', params.to.toISOString());
  url.searchParams.append('locationId', `${params.locationId}`);

  return fetchWithRetry<InvoiceItemInterface[]>(url, {}, []).then(
    invoiceItems => invoiceItems?.map(item => new InvoiceItem(item)) ?? null
  );
};

export const fetchInvoice = async (id: number): Promise<Invoice | null> => {
  const url = new URL(`/api/invoices/${id}`, window.location.origin);
  return fetchWithRetry<InvoiceInterface | null>(url, { method: 'GET' }, null).then(invoice =>
    !!invoice ? new Invoice(invoice) : null
  );
};

export const setInvoiceStatuses = async (params: {
  invoiceIds: number[];
  status: InvoiceStatus;
}): Promise<Invoice[] | null> => {
  const url = new URL('/api/invoices/status', window.location.origin);
  url.searchParams.append('status', params.status);
  params.invoiceIds.forEach(id => url.searchParams.append('invoiceIds', `${id}`));

  return fetchWithRetry<InvoiceInterface[] | null>(url, { method: 'PUT' }, null).then(
    invoices => invoices?.map(invoice => new Invoice(invoice)) ?? null
  );
};

export const setInvoiceStatusReasons = async (params: {
  invoiceIds: number[];
  statusReason: string;
}): Promise<Invoice[] | null> => {
  const url = new URL('/api/invoices/status-reason', window.location.origin);
  params.invoiceIds.forEach(id => url.searchParams.append('invoiceIds', `${id}`));

  return fetchWithRetry<InvoiceInterface[] | null>(url, { method: 'PUT', body: params.statusReason }, null).then(
    invoices => invoices?.map(invoice => new Invoice(invoice)) ?? null
  );
};


export const finalizeInvoicesForLocation = async (locationId: number): Promise<Invoice[] | null> => {
  const url = new URL(`/api/invoices/finalize/${locationId}`, window.location.origin);

  return fetchWithRetry<InvoiceInterface[] | null>(url, { method: 'PUT' }, null).then(
    invoices => invoices?.map(invoice => new Invoice(invoice)) ?? null
  );
};

export const fetchPendingInvoices = async (params: {
  pageNum?: number;
  pageSize?: number;
  from?: Date;
  to?: Date;
  sortField?: string;
  sortDirection?: string;
  filterString?: string;
  locationId?: number;
}): Promise<Page<InvoiceSummary> | null> => {
  const url = new URL('/api/invoices/pending', window.location.origin);
  url.searchParams.append('pageNum', `${params.pageNum ?? 0}`);
  url.searchParams.append('pageSize', `${params.pageSize ?? 100}`);

  if (params.sortField) {
    url.searchParams.append('sortField', params.sortField);
  }
  if (params.sortDirection) {
    url.searchParams.append('sortDirection', params.sortDirection);
  }
  if (params.filterString) {
    url.searchParams.append('filterString', params.filterString);
  }
  if (params.locationId) {
    url.searchParams.append('locationId', `${params.locationId}`);
  }

  return fetchWithRetry<Page<InvoiceSummaryInterface> | null>(url, { method: 'GET' }, null).then(page => ({
    ...page,
    content: page?.content?.map(invoice => new InvoiceSummary(invoice))
  }));
};

export const fetchFinalizedInvoices = async (params: {
  pageNum?: number;
  pageSize?: number;
  from?: Date;
  to?: Date;
  sortField?: string;
  sortDirection?: string;
  filterString?: string;
  locationId?: number;
}): Promise<Page<InvoiceSummary> | null> => {
  const url = new URL('/api/invoices', window.location.origin);
  url.searchParams.append('pageNum', `${params.pageNum ?? 0}`);
  url.searchParams.append('pageSize', `${params.pageSize ?? 100}`);

  if (params.sortField) {
    url.searchParams.append('sortField', params.sortField);
  }
  if (params.sortDirection) {
    url.searchParams.append('sortDirection', params.sortDirection);
  }
  if (params.filterString) {
    url.searchParams.append('filterString', params.filterString);
  }
  if (params.locationId) {
    url.searchParams.append('locationId', params.locationId.toString());
  }

  return fetchWithRetry<Page<InvoiceSummaryInterface> | null>(url, { method: 'GET' }, null).then(page => ({
    ...page,
    content: page?.content?.map(invoice => new InvoiceSummary(invoice))
  }));
};

export const updateInvoiceItemFreeDays = async (params: {
  invoiceItemId: number;
  freeDays: number;
}): Promise<InvoiceItem | null> => {
  const url = new URL(
    `/api/invoices/items/${params.invoiceItemId}/freeDays/${params.freeDays}`,
    window.location.origin
  );

  return fetchWithRetry<InvoiceItemInterface | null>(url, { method: 'PUT' }, null).then(item =>
    item ? new InvoiceItem(item) : null
  );
};

export const updateInvoiceItemBillableEvent = async (params: {
  invoiceItemId: number;
  billableEventId: number;
  billableEvent: BillableEvent;
}): Promise<InvoiceItem | null> => {
  const url = new URL(
    `/api/invoices/items/${params.invoiceItemId}/events/${params.billableEventId}`,
    window.location.origin
  );

  return fetchWithRetry<InvoiceItemInterface | null>(
    url,
    {
      method: 'PUT',
      body: JSON.stringify(params.billableEvent),
      headers: new Headers([['Content-Type', 'application/json']])
    },
    null
  ).then(item => (item ? new InvoiceItem(item) : null));
};

/*
 * An event the server refused to add, with its own reason: e.g. the split-loaded 409 naming the units
 * whose loaded status changed after the invoice was generated. A refusal must never look like nothing
 * happened — the form shows this message.
 */
export class InvoiceEventSaveError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'InvoiceEventSaveError';
  }
}

export const addInvoiceItemBillableEvent = async (params: {
  invoiceId: number;
  unitId: number;
  billableEvent: BillableEvent;
}): Promise<Invoice> => {
  const url = new URL(`/api/invoices/${params.invoiceId}/events`, window.location.origin);
  url.searchParams.append('unitId', `${params.unitId}`);

  const response = await authorizedFetch(url, 'POST', params.billableEvent);
  if (!response.ok) {
    throw new InvoiceEventSaveError(
      response.status,
      (await serverMessage(response)) || `The event could not be saved (${response.status}).`
    );
  }
  return new Invoice((await response.json()) as InvoiceInterface);
};

export const deleteInvoiceItemBillableEvent = async (params: { invoiceId: number; billableEventId: number }) => {
  const url = new URL(`/api/invoices/${params.invoiceId}/events/${params.billableEventId}`, window.location.origin);
  return fetchWithRetry<InvoiceInterface | null>(url, { method: 'DELETE' }, null).then(invoice =>
    invoice ? new Invoice(invoice) : null
  );
};

export const deleteInvoice = async (invoiceId: number) => {
  const url = new URL(`/api/invoices/${invoiceId}`, window.location.origin);
  return fetchWithRetry<null>(url, { method: 'DELETE' }, null);
};

export const bulkDeleteInvoices = async (ids: number[]) => {
  const url = new URL('/api/invoices/bulkDelete', window.location.origin);
  ids.forEach(id => url.searchParams.append('ids', `${id}`));
  return fetchWithRetry<null>(url, { method: 'DELETE' }, null);
};

export const downloadPdf = async (invoiceId: number) => {
  const url = new URL(`/api/invoices/${invoiceId}/pdf`, window.location.origin);
  let token = localStorage.getItem('token');
  const headers = new Headers([
    ['Accept', 'application/pdf'],
    ['Authorization', `Bearer ${token}`]
  ]);

  try {
    let response = await fetch(url, { method: 'GET', headers });
    if (response.status === 401 || response.status === 403) {
      token = await refreshToken();
      if (!token) throw new Error('Unauthenticated');
      headers.set('Authorization', `Bearer ${token}`);
      response = await fetch(url, { method: 'GET', headers });
    }

    if (response.ok) {
      let blob = await response.blob();
      const headers = response.headers;

      const filename = headers
        .get('Content-Disposition')
        ?.split(';')
        .find(h => h.includes('filename'))
        ?.split('=')
        .at(1)
        ?.replace(/"/g, '');

      const fileUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename ?? 'invoice.pdf';
      link.click();
      link.remove();
    }
  } catch (error) {
    console.error(error);
  }
};

export type InvoicePdfZip = { blob: Blob; filename: string };

/*
 * Fetches the PDFs of several finalized invoices as one ZIP. Separate from fetchWithRetry because
 * that helper only parses JSON. The Accept header allows JSON too, so validation errors from the
 * REST API still arrive as a readable error body.
 */
export const fetchInvoicePdfsZip = async (ids: number[]): Promise<InvoicePdfZip> => {
  const url = new URL('/api/invoices/bulk/pdf', window.location.origin);
  ids.forEach(id => url.searchParams.append('ids', `${id}`));

  let token = localStorage.getItem('token');
  const headers = new Headers([
    ['Accept', 'application/zip, application/json'],
    ['Authorization', `Bearer ${token}`]
  ]);

  let response = await fetch(url, { method: 'GET', headers });
  if (response.status === 401 || response.status === 403) {
    token = await refreshToken();
    if (!token) throw new Error('Unauthenticated');
    headers.set('Authorization', `Bearer ${token}`);
    response = await fetch(url, { method: 'GET', headers });
  }

  if (!response.ok) throw response;

  const filename = response.headers.get('Content-Disposition')?.match(/filename="?([^";]+)"?/)?.[1] ?? 'invoices.zip';

  return { blob: await response.blob(), filename };
};
